import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateExpectDecimalNumber,
    validateExpectPositiveNumber,
    validateStringLength30,
} from 'src/api/utils/formValidators';
import moment from 'moment';
import { PromoCodesState } from 'src/AppState/PromoCodesState/PromoCodesState';
import { WelcomeBonusesModelType } from 'src/api/config/welcome_bonuses/get_welcome_bonuses';
import { WelcomeBonusModelType } from 'src/api/config/welcome_bonuses/put_welcome_bonus';
import { UniverseType } from 'src_common/common/universe';

interface WelcomeBonusLoading {
    type: 'loading';
}
interface WelcomeBonusReady {
    type: 'ready';
    value: Array<WelcomeBonusType>;
}
interface WelcomeBonusError {
    type: 'error';
    value: string;
}

type WelcomeBonusContentResponse = WelcomeBonusLoading | WelcomeBonusReady | WelcomeBonusError;
type PageType = 'edit' | 'add';

export interface WelcomeBonusType {
    id: number | null | undefined;
    bonusId: string;
    name: string;
    universe: string;
    validUntil: string | null | undefined;
    validFor: number | null | undefined;
    minimalOdds: number;
    promotionCode: string;
    minBetAmountCents: number;
    minDepositAmountCents: number;
    maxFreebetAmountCents: number;
}
interface WelcomeBonusInnerType {
    date_to: string;
    minimum_odds: number | null;
    welcome_bonus: string;
    bonus_id: string;
    bet_amount: number | null;
    deposit_amount: number | null;
    bonus_amount: number | null;
    valid_days: number | null;
    promo_code: string;
}

interface SelectionType {
    name: string;
    key: string;
}

export class WelcomeBonusState {
    @observable private readonly welcomeBonusesResource: Resource<Array<WelcomeBonusesModelType>>;
    @observable private page: string = 'add';
    @observable private isActiveFlagChanged: boolean = false;
    @observable public editedWelcomeBonusElement: WelcomeBonusType | null = null;
    @observable public editedCode: string | null = null;
    @observable public currentWelcomeBonus: any | null = null;
    @observable public isLoading: boolean = false;
    @observable public errorMessage: string = '';
    @observable public duplicateError: string | null = null;
    @observable public validationError: string | null = null;
    @observable public isUnlimited: boolean = false;

    readonly dateToModel: FormModel<string>;
    readonly date_to: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly form: FormModel<WelcomeBonusType>;
    readonly minimumOddsModel: FormModel<number | null>;
    readonly minimum_odds: FormInputState<string>;
    readonly minimumOddsStatus: InputStatus;
    readonly welcomeBonusNameModel: FormModel<string>;
    readonly welcome_bonus: FormInputState<string>;
    readonly welcomeBonusNameStatus: InputStatus;
    readonly bonusIdModel: FormModel<string>;
    readonly bonus_id: FormInputState<string>;
    readonly bonusIdStatus: InputStatus;

    readonly betAmountModel: FormModel<number | null>;
    readonly bet_amount: FormInputState<string>;
    readonly betAmountStatus: InputStatus;

    readonly bonusAmountModel: FormModel<number | null>;
    readonly bonus_amount: FormInputState<string>;
    readonly bonusAmountStatus: InputStatus;

    readonly maxFreebetModel: FormModel<number | null>;
    readonly max_freebet_amount: FormInputState<string>;
    readonly maxFreebetStatus: InputStatus;

    readonly depositAmountModel: FormModel<number | null>;
    readonly deposit_amount: FormInputState<string>;
    readonly depositAmountStatus: InputStatus;

    readonly validDaysModel: FormModel<number | null>;
    readonly valid_days: FormInputState<string>;
    readonly validDaysStatus: InputStatus;

    readonly promoCodeModel: FormModel<string>;
    readonly promo_code: FormInputState<string>;
    readonly promoCodeStatus: InputStatus;

    constructor(
        public readonly promoCodesState: PromoCodesState,
        public readonly universe: UniverseType
    ) {
        this.welcomeBonusesResource = new Resource(async (): Promise<Array<WelcomeBonusesModelType>> => {
            this.isLoading = true;
            const response = await apiCommon.get_welcome_bonuses.run({});
            this.isLoading = false;
            return response;
        });

        this.date_to = new FormInputState('', false);
        this.dateToModel = this.date_to.toModel();
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.welcome_bonus = new FormInputState('', false);
        this.welcomeBonusNameModel = this.welcome_bonus.map(validateStringLength30);
        this.welcomeBonusNameStatus = new InputStatus(this.welcomeBonusNameModel);

        this.bonus_id = new FormInputState('', false);
        this.bonusIdModel = this.bonus_id.map(validateStringLength30);
        this.bonusIdStatus = new InputStatus(this.bonusIdModel);

        this.minimum_odds = new FormInputState('', false);
        this.minimumOddsModel = this.minimum_odds.map(validateExpectDecimalNumber);
        this.minimumOddsStatus = new InputStatus(this.minimumOddsModel);

        this.bet_amount = new FormInputState('', false);
        this.betAmountModel = this.bet_amount.map(validateExpectPositiveNumber);
        this.betAmountStatus = new InputStatus(this.betAmountModel);

        this.deposit_amount = new FormInputState('', false);
        this.depositAmountModel = this.deposit_amount.map(validateExpectPositiveNumber);
        this.depositAmountStatus = new InputStatus(this.depositAmountModel);

        this.max_freebet_amount = new FormInputState('', false);
        this.maxFreebetModel = this.max_freebet_amount.map(validateExpectPositiveNumber);
        this.maxFreebetStatus = new InputStatus(this.maxFreebetModel);

        this.bonus_amount = new FormInputState('', false);
        this.bonusAmountModel = this.bonus_amount.map(validateExpectPositiveNumber);
        this.bonusAmountStatus = new InputStatus(this.bonusAmountModel);

        this.valid_days = new FormInputState('', false);
        this.validDaysModel = this.valid_days.map(validateExpectPositiveNumber);
        this.validDaysStatus = new InputStatus(this.validDaysModel);

        this.promo_code = new FormInputState('', false);
        this.promoCodeModel = this.promo_code.map(validateStringLength30);
        this.promoCodeStatus = new InputStatus(this.promoCodeModel);

        this.form = group({
            date_to: this.dateToModel,
            minimum_odds: this.minimumOddsModel,
            welcome_bonus: this.welcomeBonusNameModel,
            bonus_id: this.bonusIdModel,
            bet_amount: this.betAmountModel,
            deposit_amount: this.depositAmountModel,
            bonus_amount: this.bonusAmountModel,
            valid_days: this.validDaysModel,
            promo_code: this.promoCodeModel,
        }).map((item: WelcomeBonusInnerType): Result<WelcomeBonusType> => {
            return new ResultValue({
                validUntil: item.date_to,
                minimalOdds: item.minimum_odds,
                name: item.welcome_bonus,
                bonusId: item.bonus_id,
                minBetAmountCents: item.bet_amount,
                maxFreebetAmountCents: item.bonus_amount,
                validFor: item.valid_days,
                promotionCode: item.promo_code,
                minDepositAmountCents: item.deposit_amount,
            });
        });
        makeAutoObservable(this);
    }

    @action public handleCancel = (): void => {
        if (this.isEditMode && this.editedWelcomeBonusElement) {
            this.setEditedWelcomeBonus(this.editedWelcomeBonusElement);
        } else {
            this.form.reset();
        }
    };

    @action public clearDate = (): void => {
        this.date_to.setValue('');
    };
    @computed get promoCodesForSelection(): Array<SelectionType> {
        const options: Array<SelectionType> = [];
        for (const codeElement of this.promoCodesState.validPromoCodeList) {
            for (const singleCode of codeElement.promo_code) {
                options.push({ name: singleCode.code, key: singleCode.code });
            }
        }
        return options;
    }

    @computed get isFormValid(): boolean {
        return this.form.result instanceof ResultValue && this.validDatesButtonDisable === false;
    }

    @action public resetForm = (): void => {
        this.setPage('add');
        this.editedWelcomeBonusElement = null;
        this.duplicateError = null;
        this.validationError = null;
        this.form.reset();
    };

    @action public setPage = (value: PageType): void => {
        this.page = value;
    };

    public checkIfDuplicate = (bonus: WelcomeBonusType, isEditMode: boolean): boolean => {
        const list = isEditMode
            ? this.welcomeBonusElementsList.filter((e) => e.id !== bonus.id)
            : this.welcomeBonusElementsList;
        for (const alreadyMadeBonus of list) {
            const isDuplicate =
                bonus.name === alreadyMadeBonus.name ||
                bonus.promotionCode === alreadyMadeBonus.promotionCode ||
                bonus.bonusId === alreadyMadeBonus.bonusId;
            if (isDuplicate) {
                return isDuplicate;
            }
        }
        return false;
    };

    @action public handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue && this.welcomeBonuses.type === 'ready') {
            const validUntil =
                result.value.validUntil !== '' && result.value.validUntil !== undefined
                    ? result.value.validUntil
                    : null;
            const validFor =
                result.value.validFor !== undefined && result.value.validFor !== null && result.value.validFor > 0
                    ? result.value.validFor
                    : null;
            const payload: WelcomeBonusType = {
                id: null,
                bonusId: result.value.bonusId,
                name: result.value.name,
                universe: this.universe,
                minimalOdds: Math.round(result.value.minimalOdds * 100),
                promotionCode: result.value.promotionCode,
                minBetAmountCents: result.value.minBetAmountCents * 100,
                minDepositAmountCents: result.value.minDepositAmountCents * 100,
                maxFreebetAmountCents: result.value.maxFreebetAmountCents * 100,
                validFor: validFor,
                validUntil: validUntil,
            };

            const submitResult = this.isEditMode
                ? await this.updateWelcomeBonus({ ...payload, id: this.editedWelcomeBonusElement?.id })
                : await this.createWelcomeBonus(payload);
            if (submitResult) {
                await this.welcomeBonusesResource.refresh();
                this.resetForm();
            } else {
                if (this.validationError === null && this.duplicateError === null) {
                    this.errorMessage = 'Unknown error. Something went wrong.';
                }
            }
        }

        this.isLoading = false;
    };

    @action public numberOfUsesIsInValid = (number_of_uses: string | undefined): boolean => {
        const allNotFilled = (number_of_uses === undefined || number_of_uses === null) && this.isUnlimited === false;
        const allFilled = number_of_uses !== null && number_of_uses !== undefined && this.isUnlimited === true;

        if (allNotFilled || allFilled) {
            return true;
        }
        return false;
    };

    public isDateValid = (dateFrom: string, dateTo: string): boolean => {
        return moment(dateTo).isAfter(dateFrom);
    };

    @computed get isValidDate(): boolean {
        return (
            (this.valid_days.value === '' && this.date_to.value === '') ||
            this.date_to.value === '' ||
            this.valid_days.value === ''
        );
    }

    @computed public get validDatesButtonDisable(): boolean {
        const isValidDate =
            (this.valid_days.value !== '' && this.date_to.value !== '') ||
            (this.valid_days.value === '' && this.date_to.value === '');
        return isValidDate;
    }

    @computed public get invalidDates(): boolean {
        const isInValidDate = this.valid_days.value !== '' && this.date_to.value !== '';
        return isInValidDate;
    }

    @action private createWelcomeBonus = async (
        welcomeBonus: WelcomeBonusType
    ): Promise<WelcomeBonusModelType | null> => {
        let response: WelcomeBonusModelType;

        try {
            const isDuplicate = this.checkIfDuplicate(welcomeBonus, false);
            if (isDuplicate) {
                this.duplicateError =
                    'There is existing welcome bonus with the same promo code / bonus id / bonus name.';
                return null;
            }

            response = await apiCommon.put_welcome_bonus.run({ data: welcomeBonus });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private updateWelcomeBonus = async (
        welcomeBonus: WelcomeBonusType
    ): Promise<WelcomeBonusModelType | null> => {
        let response: WelcomeBonusModelType;

        try {
            const isDuplicate = this.checkIfDuplicate(welcomeBonus, true);
            if (isDuplicate) {
                this.duplicateError =
                    'There is existing welcome bonus with the same promo code / bonus id / bonus name.';
                return null;
            }

            response = await apiCommon.put_welcome_bonus.run({ data: welcomeBonus });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action public onRemove = async (welcomeBonus: WelcomeBonusType): Promise<void> => {
        const id = welcomeBonus.id;
        const welcomeBonusListItem = this.welcomeBonusElementsList.find((e) => e.id === id);

        if (welcomeBonusListItem && id !== undefined && id !== null) {
            const removeResult = await apiCommon.delete_welcome_bonus.run({ id });

            if (removeResult) {
                await this.welcomeBonusesResource.refresh();

                this.form.reset();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }
    };

    @action public setEditedWelcomeBonus = (bonus: WelcomeBonusType): void => {
        this.duplicateError = null;
        this.validationError = null;

        this.editedWelcomeBonusElement = bonus;
        this.welcome_bonus.setValue(bonus.name);
        this.date_to.setValue(bonus.validUntil ?? '');
        this.minimum_odds.setValue((bonus.minimalOdds / 100).toFixed(2).toString());
        this.bonus_id.setValue(bonus.bonusId);
        this.deposit_amount.setValue((bonus.minDepositAmountCents / 100).toString());
        this.bet_amount.setValue((bonus.minBetAmountCents / 100).toString());
        this.bonus_amount.setValue((bonus.maxFreebetAmountCents / 100).toString());
        this.valid_days.setValue(bonus.validFor?.toString() ?? '');
        this.promo_code.setValue(bonus.promotionCode);
    };

    @computed private get welcomeBonuses(): WelcomeBonusContentResponse {
        const welcomeBonuses = this.welcomeBonusesResource.get();
        if (welcomeBonuses.type === 'ready' && welcomeBonuses.value !== null) {
            const welcomeBonusesObject = welcomeBonuses.value;

            return {
                type: 'ready',
                value: welcomeBonusesObject,
            };
        }
        if (welcomeBonuses.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching promo codes info',
        };
    }

    public checkIfIsActive = (welcomeBonus: WelcomeBonusType) => {
        if (
            welcomeBonus.validUntil === null ||
            welcomeBonus.validUntil === undefined ||
            welcomeBonus.validUntil === ''
        ) {
            return false;
        }
        const endDate = moment(welcomeBonus.validUntil);
        const currentDate = moment();
        const isDateBefore = moment(endDate).isBefore(currentDate);

        return isDateBefore === false;
    };

    @computed public get welcomeBonusElementsList(): Array<WelcomeBonusType> {
        if (this.welcomeBonuses.type === 'ready') {
            return this.welcomeBonuses.value.sort((a: WelcomeBonusType, b: WelcomeBonusType) => {
                const aName = a.name;
                const bName = b.name;
                return aName.localeCompare(bName);
            });
        }
        return [];
    }

    @computed public get isEditMode(): boolean {
        return this.page === 'edit' || this.isActiveFlagChanged;
    }

    @computed public get isFormModified(): boolean {
        return this.form.modifiedStatus;
    }

    @computed private get parsedEndDate(): number | null {
        return this.date_to.value !== null ? Date.parse(this.date_to.value) : null;
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }

    static createForContext(): WelcomeBonusState {
        return new WelcomeBonusState(new PromoCodesState(), 'star');
    }
}
